import { type FC } from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";

import {
  COLOR_PICKER_COLUMNS,
  COLOR_PICKER_SWATCHES,
} from "./colorPickerPalette";

import styles from "./ColorPickerPopover.module.css";

type ColorPickerPopoverProps = {
  /** Currently selected hex (highlights the matching cell). */
  value: string;
  onSelect: (hex: string) => void;
  onClose: () => void;
};

/**
 * "Select a Color" grid popover. A transparent backdrop closes it on an
 * outside click; the grid is a fixed generated palette (greyscale row + hue ×
 * shade matrix). Anchored by the caller relative to the triggering swatch.
 */
const ColorPickerPopover: FC<ColorPickerPopoverProps> = ({
  value,
  onSelect,
  onClose,
}) => {
  const selected = value.trim().toUpperCase();

  return (
    <>
      <button
        type="button"
        className={styles.backdrop}
        onClick={onClose}
        aria-label={AppStrings.CLOSE}
        data-testid="color-picker-backdrop"
      />
      <dialog
        open
        className={styles.popover}
        aria-label={AppStrings.GAME_PASS_STYLE_COLOR_PICKER_TITLE}
      >
        <div className={styles.header}>
          <span className={styles.title}>
            {AppStrings.GAME_PASS_STYLE_COLOR_PICKER_TITLE}
          </span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={AppStrings.CLOSE}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div
          className={styles.grid}
          style={{
            gridTemplateColumns: `repeat(${COLOR_PICKER_COLUMNS}, 1fr)`,
          }}
        >
          {COLOR_PICKER_SWATCHES.map((hex, index) => (
            <button
              type="button"
              key={`${hex}-${index}`}
              className={`${styles.cell} ${selected === hex ? styles.cellActive : ""}`}
              style={{ backgroundColor: hex }}
              onClick={() => onSelect(hex)}
              aria-label={hex}
            />
          ))}
        </div>
      </dialog>
    </>
  );
};

export default ColorPickerPopover;
